/**
 * What may be uploaded for identity verification.
 *
 * Cashfree's OCR reads JPEG, JPG and PNG and refuses everything else —
 * a PAN card saved as AVIF came back "Provide valid file or file url",
 * which the screen shows as its manual-entry fallback, so it read as a
 * document that failed verification rather than a file we never sent.
 *
 * So the picker only offers what will actually work. The alternative —
 * accepting anything and converting — means the desk finds out at the
 * counter, after the upload, that the file was never readable.
 */

/** For the `accept` attribute: the browser's own file dialog filter. */
export const ACCEPTED_DOCUMENT_TYPES = "image/jpeg,image/png,.jpg,.jpeg,.png";

const ALLOWED_MIME = new Set(["image/jpeg", "image/png"]);
const ALLOWED_EXTENSION = /\.(jpe?g|png)$/i;

export const ACCEPTED_DOCUMENTS_MESSAGE =
  "Upload a JPEG or PNG. Other formats cannot be read by the verification service.";

/**
 * Whether this file can be verified.
 *
 * Both the type and the name are checked: a browser can report an empty
 * or wrong MIME type for a file dragged in or renamed, and the `accept`
 * attribute is a filter on the dialog, not a guarantee — a file can
 * still arrive by drag and drop.
 */
export function isAcceptedDocument(file: File): boolean {
  if (ALLOWED_MIME.has(file.type)) return true;
  // No type reported at all: fall back to the name rather than refusing
  // a file that is probably fine.
  if (!file.type) return ALLOWED_EXTENSION.test(file.name);
  return false;
}
